import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import ScrollableDropShadow from '@ui/ScrollableDropShadow.tsx';
import SearchResults from '@ui/SearchResults/SearchResults.tsx';

import { useIsInitialState } from '../../../../src/hooks/useIsInitialState.tsx';
import { focusElement } from '../../../utils/focusUtils.ts';

const SearchResultsStyled = styled(SearchResults)`
  overflow: auto;
`;

const SearchResultView = ({
  isDesktopFn,
  widgetState,
  handleSuggestionClicked = () => {},
  handlePoiClicked = () => {},
  handleEscapeKey = () => {},
  T,
}) => {
  const [placesNearby, setPlacesNearby] = useState(widgetState.getState().nearby);
  const [locations, setLocations] = useState(widgetState.getState().locations);
  const [searchSuggestions, setSearchSuggestions] = useState(widgetState.getState().keywords);
  const [isSearchConfirmed, setIsSearchConfirmed] = useState(widgetState.getState().isSearchConfirmed);
  const [openStatuses, setOpenStatuses] = useState(widgetState.getState().openStatuses);

  useEffect(
    () =>
      widgetState.addCallback(({ nearby, locations, isSearchConfirmed }) => {
        setPlacesNearby(nearby);
        setLocations(locations);
        setSearchSuggestions(widgetState.getState().keywords);
        setIsSearchConfirmed(isSearchConfirmed);
        setOpenStatuses(widgetState.getState().openStatuses);
      }),
    [widgetState],
  );

  const shouldAutoFocus = !useIsInitialState([isSearchConfirmed]);

  useEffect(() => {
    if (
      shouldAutoFocus &&
      (placesNearby?.length || locations?.length) &&
      // focus if nothing else has focus or the search is confirmed (enter pressed)
      (document.activeElement === document.body || isSearchConfirmed)
    ) {
      // Defer focus to the next tick to prevent race conditions with other UI elements (like Toasts).
      setTimeout(() => {
        focusElement('[data-testid="searchResultsElementPoi"]');
      }, 0);
    }
  }, [placesNearby, locations, isSearchConfirmed, shouldAutoFocus]);

  const isDesktop = isDesktopFn();

  return (
    <ScrollableDropShadow T={T} isDesktop={isDesktop}>
      <SearchResultsStyled
        placesNearby={placesNearby}
        placesNearbyTitle={T('homeView:NEARBY LOCATIONS')}
        placesTitle={T('homeView:LOCATIONS')}
        locations={locations}
        openStatuses={openStatuses}
        searchSuggestions={!isSearchConfirmed ? searchSuggestions : null}
        onSuggestionClicked={handleSuggestionClicked}
        onPoiClicked={handlePoiClicked}
        isDesktop={isDesktop}
        hasBottomPadding={!isDesktop && isSearchConfirmed}
        handleEscapeKey={handleEscapeKey}
        T={T}
      />
    </ScrollableDropShadow>
  );
};

SearchResultView.propTypes = {
  handleSuggestionClicked: PropTypes.func,
  handlePoiClicked: PropTypes.func,
  widgetState: PropTypes.shape({
    getState: PropTypes.func.isRequired,
    update: PropTypes.func.isRequired,
  }).isRequired,
  isDesktopFn: PropTypes.func.isRequired,
  handleEscapeKey: PropTypes.func,
};

export default SearchResultView;
